import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import * as actions from '../../../actions'
import axios from 'axios'
import { format } from 'date-fns'
import { saveAs } from 'file-saver'
import writeXlsxFile from 'write-excel-file'
import ResellersTable from './ResellersTable'
import ConsumersTable from './ConsumersTable'
import { Container, Grid, GridColumn, Input, Button, Icon, Menu, MenuItem, Loader } from 'semantic-ui-react'

const Orders = () => {
    const [isSearching, setIsSearching] = useState(false)
    const [isLoading, setIsLoading] = useState(true) 
    const [isRefreshingAllStatus, setIsRefreshingAllStatus] = useState(false)
    const [activeItem, setActiveItem] = useState('resellers')
    const [dataResellers, setDataResellers] = useState([])
    const [dataConsumers, setDataConsumers] = useState([])
    const [searchString, setSearchString] = useState('')

    const orders = useSelector(state => state.orders)
    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(actions.fetchOrders(true))
    }, [dispatch])

    useEffect(() => {
        if (orders) {
            const newDataResellers = []
            const newDataConsumers = []
            for (const order of orders) {
                if (order.buyerType === 'consumer') newDataConsumers.push(order)
                else newDataResellers.push(order)
            }
            setDataResellers(newDataResellers)
            setDataConsumers(newDataConsumers)
            setIsLoading(false)
        }
    }, [orders])

    const getSearchedOrders = () => {
        if (activeItem === 'resellers') {
            return dataResellers.filter(order =>
                ['id', 'game', 'package', 'userName', 'userID', 'serverID', 'status', 'reseller.name', 'item', 'supplierOrderID'].some(field => {
                    const value = field.includes('.') ? field.split('.').reduce((obj, key) => obj?.[key], order) : order[field]
                    return value?.toLowerCase?.()?.includes(searchString.toLowerCase())
                })
            )
        } else {
            return dataConsumers.filter(order =>
                ['id', 'game', 'package', 'userName', 'userID', 'serverID', 'status', 'consumer.name', 'consumer.email', 'consumer.phone', 'item', 'supplierOrderID'].some(field => {
                    const value = field.includes('.') ? field.split('.').reduce((obj, key) => obj?.[key], order) : order[field]
                    return value?.toLowerCase?.()?.includes(searchString.toLowerCase())
                })
            )
        }
    }

    const handleMenuItemClicked = e => {
        switch (e.target.innerText) {
            case 'Resellers':
                setActiveItem('resellers')
                break
            case 'Consumers':
                setActiveItem('consumers')
                break
            default:
                break
        }
    }

    const showRefreshAllStatusButton = () => {
        let counter = 0
        for (const order of orders || []) {
            if (showRefreshButtonForOrder(order)) counter += 1
        }
        return counter > 1
    }

    const showRefreshButtonForOrder = order => {
        return order.status === 'new' || order.status === 'pending'
    }

    const handleChangeSearchField = e => {
        const { value } = e.target
        if (value === '') {
            setIsSearching(false)
        } else {
            setIsSearching(true)
            setSearchString(value)
        }
    }

    const handleRefreshOrderStatus = order => {
        const dict = { denom: order.denom }
        axios.post('/api/reseller/order/' + order.id + '/refresh_status', dict)
            .then(res => {
                dispatch(actions.fetchOrders(true))
            })
            .catch(err => {
                console.log(`POST /api/reseller/order error: ${err}`)
                alert(`Failure refreshing order status, please try again. Error: ${err}`)
            })
    }

    const handleRefreshAllStatusButtonClicked = () => {
        setIsRefreshingAllStatus(true)
        axios.post('/api/reseller/orders/refresh_all_status')
            .then(res => {
                setIsRefreshingAllStatus(false)
                dispatch(actions.fetchOrders(true))
            })
            .catch(err => {
                setIsRefreshingAllStatus(false)
                console.log(`POST api/reseller/orders/refresh_all_status error: ${err}`)
                alert(`Failure refreshing order status, please try again. Error: ${err}`)
            })
    }

    const handleExportToExcel = async orders => {
        const sheets = ['Orders']
        const data = []
        const headerRow = generateHeaderRowForSheet(sheets[0])
        data.push(headerRow)
        for (const order of orders) {
            const profit = order.amount && order.cost ? Number(order.amount) - Number(order.cost) : 0
            const arr = [order.id, format(new Date(order.date), 'd MMM yyyy, h:mm a'), order.game, order.package, order.userName, order.userID, order.serverID, order.item, order.amount, order.cost || '-', profit, 
                        order.reseller.name, order.reseller.oldBalance, order.reseller.newBalance, order.status]
            const row = arr.map(value => ({ value }))
            data.push(row)
        }
        const blob = await writeXlsxFile([data], { sheets: sheets, stickyRowsCount: 1 })
        const formattedDate = format(new Date(), 'd MMMM yyyy hh-mm a')
        saveAs(blob, `EGC-Orders-${formattedDate}.xlsx`)
    }

    const generateHeaderRowForSheet = () => {
        const arr = ['Order ID', 'Date', 'Game', 'Package', 'User Name', 'User ID', 'Server ID', 'Item', 'Amount', 'Cost', 'Profit', 'Reseller', 'Old Balance', 'New Balance', 'Status']
        return arr.map(value => ({ value, fontWeight: 'bold' }))
    }

    return (
        <Container inverted style={{ background: '#1b1c1d', paddingTop: 12 }}>
            <Grid>
                <GridColumn floated='left' width={5}>
                    <h1 style={{ color: 'white' }}>Orders</h1>
                </GridColumn>
                <GridColumn width={6}>
                    <Input inverted fluid action={{ icon: 'search', inverted: true, color: 'teal' }} placeholder='Search order...' onChange={handleChangeSearchField} style={{ border: '1px #333 solid' }} input={<input style={{ backgroundColor: '#1b1c1d', color: 'white' }} />}/>
                </GridColumn>
                <GridColumn floated='right' width={5}>
                    {showRefreshAllStatusButton() ? (
                        <Button inverted size={window.innerWidth <= 760 ? 'mini' : null} color='teal' style={{ marginLeft: 15, marginRight: 15 }} loading={isRefreshingAllStatus} onClick={handleRefreshAllStatusButtonClicked}>
                            <Icon inverted color='teal' name='refresh' />Refresh All Status
                        </Button>
                    ) : null}
                    <Button inverted size={window.innerWidth <= 760 ? 'mini' : null} color='teal' floated='right' onClick={() => handleExportToExcel(orders)}>
                        <Icon inverted color='teal' name='cloud download' />Export
                    </Button>
                </GridColumn>
            </Grid>
            <Menu size={window.innerWidth < 768 ? 'mini' : null} inverted pointing secondary>
                <MenuItem name='resellers' active={activeItem === 'resellers'} onClick={handleMenuItemClicked}/>
                <MenuItem name='consumers' active={activeItem === 'consumers'} onClick={handleMenuItemClicked}/>
            </Menu>
            <Loader inverted active={isLoading} size='huge'>Loading</Loader>
            {activeItem === 'resellers' ? (
                <ResellersTable data={isSearching ? getSearchedOrders() : dataResellers} refreshClicked={handleRefreshOrderStatus}/>) 
                : (<ConsumersTable data={isSearching ? getSearchedOrders() : dataConsumers} refreshClicked={handleRefreshOrderStatus}/>)}
        </Container>
    )
}

export default Orders